import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { User } from '@lingugeek/schemas';
import { api } from '@/lib/api';
import { API_ENDPOINTS } from '@/config/api';
import { AuthContext } from '@/contexts/AuthContext';

export default function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();

  const fetchUser = useCallback(async () => {
    try {
      const data = await api.getUser();
      return data as User;
    } catch (error) {
      // Handle structured errors
      const hasErrorCategory = (err: unknown): err is { category: string } => {
        return typeof err === 'object' && err !== null && 'category' in err;
      };

      const hasStatusCode = (err: unknown): err is { statusCode: number } => {
        return typeof err === 'object' && err !== null && 'statusCode' in err;
      };

      if (hasErrorCategory(error)) {
        // If it's an authentication error, return null (user not authenticated)
        if (error.category === 'authentication') {
          return null;
        }
        // If it's a network error with 401 status, return null
        if (
          error.category === 'network' &&
          hasStatusCode(error) &&
          error.statusCode === 401
        ) {
          return null;
        }
      }

      // Fallback for legacy string-based error detection
      if (error instanceof Error && error.message.includes('401')) {
        return null;
      }

      throw error;
    }
  }, []);

  const userQuery = useQuery<User | null>({
    queryKey: ['user'],
    queryFn: fetchUser,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const login = useCallback(
    async (googleToken: string) => {
      const res = await fetch(API_ENDPOINTS.GOOGLE_AUTH, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: googleToken }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        try {
          const userData = await api.getUser();
          queryClient.setQueryData(['user'], userData);
        } catch (error) {
          console.error('Failed to fetch user data after login:', error);
          throw new Error('Login successful but failed to fetch user data');
        }
      } else {
        throw new Error(data.error || 'Login failed');
      }
    },
    [queryClient]
  );

  const logout = useCallback(async () => {
    await api.logout();
    queryClient.setQueryData(['user'], null);
    // Navigation will be handled by the component that calls logout
  }, [queryClient]);

  const updateUser = useCallback(
    (updates: Partial<User>) => {
      queryClient.setQueryData(['user'], (prev: User | null) =>
        prev ? { ...prev, ...updates } : prev
      );
    },
    [queryClient]
  );

  return (
    <AuthContext.Provider
      value={{
        user: userQuery.data ?? null,
        loading: userQuery.isLoading || userQuery.isFetching,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
